import { useEffect, useState } from 'react';

import { useHelpBundle } from '@plasma/ui.application.app';
import { useParams, useNavigate } from '@plasma/ui.application.router';
import notification from '@plasma/ui.feedback.notification';
import { useResourceBundle, useTranslation } from '@plasma/ui.utils.i18n';

import { fetchAllDataGet_byApiName, fetchAllDataGet_byApiName_for_RFQ } from '../../services/gets/getdata';
import { sendAllDataPost_byApiNameAndData } from '../../services/posts/postdata';
import translations from './i18n';
import { PlannedDemandPageProps } from './planned-demand-page';
import help from './planned-demand-page.help';
import { PlanSnapshot, ProductionPlan } from '../../services/Interface/plannedDemandInterface';
import { apiUriCartDataHttp, apiUriConsumableDataHttp, apiUriMasterDataHttp, apiUriProductionPlanHttp } from '../../services/conf/apiConf';

function usePlannedDemandPageHook(props: PlannedDemandPageProps) {
  useResourceBundle(translations, undefined, '30ba6f85-f869-4f8d-849f-c3bbcb5ea857');
  useHelpBundle(help);

  const { t } = useTranslation();
  const navigate = useNavigate();
  const { planId, suffix, planName } = useParams();



  // --- UI State ---
  const [iFrameSrc, setIframeSrc] = useState("");
  const [iFrameVisible, setIframeVisible] = useState(false);
  const [isPdfLoading, setIsPdfLoading] = useState(false);
  const [itemsAlreadyInCart, setItemsAlreadyInCart] = useState(0);

  //user States
  const [userRoles, setuserRoles] = useState<string[]>([]);
  const isSuperUser = sessionStorage.getItem('superUserRole');

  // --- Data State ---
  const [selectedSnapshotId, setSelectedSnapshotId] = useState<string>(planId || '');
  const [selectedPlanData, setSelectedPlanData] = useState<ProductionPlan>();
  const [selectedPlanDemand, setSelectedPlanDemand] = useState<any>();
  const [productWiseDistributionData, setProductWiseDistributionData] = useState<any>(null);
  const [planSnapshotsData, setPlanSnapshotsData] = useState<any[]>([]);
  const [listOfPlans, setListOfPlans] = useState<{ key: string, value: string }[]>([]);

  const [isTableLoading, setIsTableLoading] = useState(false);

  useEffect(() => {
    if (selectedSnapshotId) {
      fetchProductionPlanById(selectedSnapshotId);
    }

  }, [selectedSnapshotId]);

  useEffect(() => {
    fetchSnapshots();
    const storedData = sessionStorage.getItem('UserRoles');
      console.log("storedData",storedData);
      if (storedData) {
        setuserRoles(JSON.parse(storedData));
      }
  }, []);

    const fetchSnapshots = async () => {
      if (!planId) return;
      try {
        const data = await fetchAllDataGet_byApiName(`GetPlanSnapshots?templateId=${planId}`, apiUriProductionPlanHttp());
        setPlanSnapshotsData(data || []);
      } catch (error) {
        console.error('Failed to fetch snapshots:', error);
      }
    };

  useEffect(() => {
    const options = [{ key: planId || '', value: `Plan: ${planName}` }];

    if (planSnapshotsData && planSnapshotsData.length > 0) {
      const snapshotOptions = planSnapshotsData.map((d: any) => ({
        key: d.SnapshotId || d.snapshot_id,
        value: `Calculation: ${d.SnapshotName || d.snapshot_name}`
      }));
      options.push(...snapshotOptions);
    }

    setListOfPlans(options);

    if (suffix && suffix !== '0' && planSnapshotsData.length > 0) {
      const selectedSuffixPlan = planSnapshotsData.find((item: any) => {
        const snapshotName = item.SnapshotName || item.snapshot_name || '';
        return snapshotName.toLowerCase().includes(suffix.toLowerCase());
      });

      const targetId = selectedSuffixPlan?.SnapshotId || selectedSuffixPlan?.snapshot_id;

      if (targetId && targetId !== selectedSnapshotId) {
        handleSnapshotChange(targetId);
      }
    }
  }, [planSnapshotsData, planId, planName, suffix]);



  const fetchProductionPlanById = async (planIdLocal: string) => {
    if (!planIdLocal) {
      notification.error("Plan Details not Provided");
      return;
    }

    setIsTableLoading(true);

    try {
      const responsePlan = await fetchAllDataGet_byApiName(`GetProductionPlan?planId=${planIdLocal}`, apiUriProductionPlanHttp());
      setSelectedPlanData(responsePlan);

      const [productDetails, demandData, cartData] = await Promise.all([
        fetchAllDataGet_byApiName("GetMasterProductDetails", apiUriMasterDataHttp()),
        fetchAllDataGet_byApiName(`GetConsumableDemand?PlanId=${planIdLocal}`, apiUriConsumableDataHttp()),
        fetchAllDataGet_byApiName('getItemInShoppingCartToEdit', apiUriCartDataHttp())
      ]);

      const mappedData = responsePlan.productWiseDistributePlan?.map((item: any) => ({
        ...item,
        productCode: productDetails?.find((detail: any) => detail.productcode === item.productCode)?.productdescription || item.productCode
      }));

      setProductWiseDistributionData(mappedData);
      setSelectedPlanDemand(demandData?.demandSummary);

    const groupedSums = cartData.reduce((acc: Record<string, number>, item: any) => {
      const code = item.itemcode;
      acc[code] = (acc[code] || 0) + item.itemcount;
      return acc;
    }, {});

    const totalItems = Object.values(groupedSums).reduce((sum: number, groupTotal: any) => {
      return sum + Math.ceil(groupTotal);
    }, 0);

    setItemsAlreadyInCart(totalItems);

    } catch (error) {
      console.error('Error fetching production plan data:', error);
      notification.error('Error', 'Failed to fetch plan data');
    }
    finally{
      setIsTableLoading(false);
    }
  };

  const fetchAndSetIframeSource = async () => {
    notification.info("Waiting for File");
    setIsPdfLoading(true);

    try {
      const { headers, data } = await fetchAllDataGet_byApiName_for_RFQ(`GeneratePlannedDemandReport?planId=${selectedSnapshotId}`, apiUriProductionPlanHttp());

      notification.info("PDF received");

      const contentDisposition = headers.get('Content-Disposition');
      let filename = 'default_filename.pdf';

      if (contentDisposition) {
        const matches = contentDisposition.match(/filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/);
        if (matches != null && matches[1]) {
          filename = matches[1].replace(/['"]/g, '');
        }
      }

      setIframeSrc(data);
      setIframeVisible(true);
    } catch (error) {
      notification.error('Error!', 'Unsuccessful Attempt', undefined, 'medium', 2);
    } finally {
      setIsPdfLoading(false);
    }
  };

  const handleAddToCart = async () => {
    setIsTableLoading(true);
    try {
      const demandData = await fetchAllDataGet_byApiName(`GetConsumableDemand?PlanId=${selectedSnapshotId}`, apiUriConsumableDataHttp());

      const demandDetailsWithSource = demandData.demandDetails.map((detailsItem: any) => ({
        ...detailsItem,
        source: `PLAN - ${selectedSnapshotId}`
      }));

      console.log(demandDetailsWithSource);

      const postResponse = await sendAllDataPost_byApiNameAndData('CreateConsumableDemandDetails', demandDetailsWithSource, apiUriConsumableDataHttp());

      if (postResponse) {
        navigate(`/shopping-cart/${planId}/${suffix}/${planName}`);
      }
    } catch (error) {
      console.error('Failed to add items to cart:', error);
      notification.error('Error', 'Failed to add items to cart');
    } finally{
      setIsTableLoading(false);
    }
  };

  const handleDeleteSnapShot = async () => {
    if (!selectedPlanData) return;

    const submitData = {
      id: selectedPlanData.id,
      planId: selectedPlanData.planId,
      planName: selectedPlanData.planName,
      plannedTonnage: selectedPlanData.plannedTonnage,
      plannedPercent: 100,
      plannedDate: '',
      plannedBy: "",
      plannedForQuarter: selectedPlanData.plannedForQuarter,
      plannedForMonth: selectedPlanData.plannedForMonth,
      plannedForYear: selectedPlanData.plannedForYear,
      planFromDate: selectedPlanData.planFromDate,
      planToDate: selectedPlanData.planToDate,
      planType: selectedPlanData.planType,
      IsActive: true,
      remarks: null,
      ProductWiseDistributePlan: []
    };

      setSelectedPlanData(undefined);
      setProductWiseDistributionData(null);
      setSelectedPlanDemand(null);

    try {
      await sendAllDataPost_byApiNameAndData('delete', submitData, apiUriProductionPlanHttp());
      notification.success('Success', 'Calculation plan deleted successfully');

    if (planId) {
      await fetchSnapshots();
      setSelectedSnapshotId(planId);
    }
    } catch (error) {
      console.error('Failed to delete snapshot:', error);
      notification.error('Error', 'Failed to delete snapshot');
    }
  };

  // --- Simple Handlers ---

  const handleAdjustProdMix = () => {
    navigate('/production-figure');
  };

  const handleJustGoToCart = () => {
    navigate(`/shopping-cart/${planId}/${suffix}/${planName}`);
  };

  const handleSnapshotChange = (value: string) => {
    setSelectedSnapshotId(value || planId || '');
  };

  return {
    t,
    handleAdjustProdMix,
    selectedPlanData,
    selectedPlanDemand,
    productWiseDistributionData,
    iFrameSrc,
    iFrameVisible,
    itemsAlreadyInCart,
    isPdfLoading,
    setIframeVisible,
    fetchAndSetIframeSource,
    handleAddToCart,
    handleJustGoToCart,
    planSnapshotsData,
    selectedSnapshotId,
    handleSnapshotChange,
    listOfPlans,
    handleDeleteSnapShot,
    isTableLoading,
    userRoles,
    isSuperUser

  };
}

export default usePlannedDemandPageHook;
